import is from '@sindresorhus/is';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useSnackbars } from '../../../app/error-handling/SnackBars';
import type { Comment } from '../../../data-contract/comment';
import { useApplicationFetchers } from '../../../fetchers/use-application-fetchers';
import { allLanguageCommentsQueryKey, commentsQueryKey, commentsQueryKeyPrefix } from './use-comments-query';
import { repliesQueryKey } from './use-replies-query';

type UseDeleteCommentParams = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly language: string;
    readonly version: number;
};

export type DeleteCommentVariables = {
    readonly commentId: string;
};

type DeleteCommentContext = {
    readonly hadPreviousAllLanguageCommentsQuery: boolean;
    readonly previousAllLanguageComments: readonly Comment[] | undefined;
    readonly previousComments: readonly Comment[] | undefined;
};

export const useDeleteComment = ({
    architectureId,
    itemId,
    language,
    version,
}: UseDeleteCommentParams): UseMutationResult<unknown, Error, DeleteCommentVariables, DeleteCommentContext> => {
    const { deleteComment } = useApplicationFetchers();
    const queryClient = useQueryClient();
    const { showErrorSnackbar } = useSnackbars();

    const commentsKey = commentsQueryKey({ architectureId, itemId, language, version });
    const allLanguageCommentsKey = allLanguageCommentsQueryKey({ architectureId, itemId, version });

    return useMutation<unknown, Error, DeleteCommentVariables, DeleteCommentContext>({
        mutationKey: ['delete-comment', architectureId, itemId, version],
        mutationFn: async ({ commentId }) => {
            const result = await deleteComment({ architectureId, commentId, itemId });
            if (result.isErr) {
                throw result.error;
            }
            return result.value;
        },
        onError: (error, _variables, context) => {
            if (is.undefined(context)) return;
            queryClient.setQueryData<readonly Comment[]>(commentsKey, context.previousComments);
            if (context.hadPreviousAllLanguageCommentsQuery) {
                queryClient.setQueryData<readonly Comment[]>(
                    allLanguageCommentsKey,
                    context.previousAllLanguageComments,
                );
            } else {
                queryClient.removeQueries({ queryKey: allLanguageCommentsKey });
            }
            showErrorSnackbar(error);
        },
        onMutate: async ({ commentId }) => {
            await Promise.all([
                queryClient.cancelQueries({ queryKey: commentsKey }),
                queryClient.cancelQueries({ queryKey: allLanguageCommentsKey }),
            ]);

            const previousComments = queryClient.getQueryData<readonly Comment[]>(commentsKey);
            const previousAllLanguageComments = queryClient.getQueryData<readonly Comment[]>(allLanguageCommentsKey);
            const hadPreviousAllLanguageCommentsQuery = !is.undefined(
                queryClient.getQueryState(allLanguageCommentsKey),
            );

            queryClient.setQueryData<readonly Comment[]>(commentsKey, (prev) =>
                is.undefined(prev) ? prev : prev.filter((comment) => comment.id !== commentId),
            );
            queryClient.setQueryData<readonly Comment[]>(allLanguageCommentsKey, (prev) =>
                is.undefined(prev) ? prev : prev.filter((comment) => comment.id !== commentId),
            );

            return { previousComments, previousAllLanguageComments, hadPreviousAllLanguageCommentsQuery };
        },
        onSuccess: (_data, variables) => {
            queryClient.removeQueries({
                queryKey: repliesQueryKey({
                    architectureId,
                    itemId,
                    parentCommentId: variables.commentId,
                    version,
                }),
            });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: commentsQueryKeyPrefix(architectureId, itemId, version) });
        },
        throwOnError: false,
    });
};
